// THE STANDING BRIEF ('#person?id=N') — everything the record holds about one human, arranged
// so the operator can act without reading it all.
//
// It answers four questions in this order, and the order is the argument:
//
//   NEXT MOVE            what to do now, composed server-side from the open commitments and
//                        rendered UNEDITED. One card, one edge, one verb.
//   WHY NOW              the scoring agent's reason to act (components/WhyNow.tsx), rules
//                        first and the model's two sentences second, ◇ when a model wrote it.
//   HOW YOU TWO WRITE    the writing profile, measured by the mail service (components/
//                        WritingProfile.tsx). Under two messages every value reads "not
//                        collected": a thing nobody has measured is not a thing measured at
//                        zero (ATTEST B8).
//   LATEST               the interactions, newest first, through components/Timeline.tsx.
//   DOSSIER · PERSONA    the research document (components/DossierZone.tsx — a document,
//                        never held) and the proposed persona (components/PersonaZone.tsx).
//   WHAT WE CURRENTLY KNOW   every current fact and every affiliation, in two columns, each
//                        carrying its claim mark — ▪ an operator holds it, ◇ somebody
//                        asserted it and nobody has. The ◇ rows carry the Confirm that turns
//                        one into the other. That button is the whole of P1 on this page.
//                        Since 2026-09-06 the record comes FIRST, and since round 7 it opens
//                        with the CONTACT CARD (components/ContactCard.tsx): e-mail, phone,
//                        LinkedIn, canton and commune of work, language, register, tier,
//                        cadence, do not contact — each with its mark and its method, each
//                        editable inline through PATCH, which writes declared facts.
//   PROFILE              the person raster (components/PersonRasterZone.tsx, BUILD-BRIEF-R7
//                        §6.1): five blocks, every cell in its state. Its "Fill" verb and the
//                        mast's "Research" verb are ONE function on this page.
//
// NOTHING HERE DERIVES A CONDITION. `cooling`, `late`, the rank, the next move and the counts
// all arrive from GET /api/persons/:id already decided (src/server/routes/persons.ts), so this
// page and the board cannot come to different conclusions about the same person.
import { useCallback, useMemo, useState, type ComponentType, type ReactNode } from 'react';
import ConfirmModal from '../components/ConfirmModal.js';
import ContactCard from '../components/ContactCard.js';
import DossierZone from '../components/DossierZone.js';
import EmptyState from '../components/EmptyState.js';
import { PersonAvatar } from '../components/Identity.js';
import PersonaZone from '../components/PersonaZone.js';
import PersonRasterZone, { cellOf } from '../components/PersonRasterZone.js';
import WhyNow from '../components/WhyNow.js';
import WritingProfile from '../components/WritingProfile.js';
import {
  Att, Fig, Lnk, Mast, PageShell, Rail, RailCell, Ref, Sep, Sheet, Tag, Zone, ZoneHead,
} from '../components/PageKit.js';
import TimelineComponent from '../components/Timeline.js';
import { useToast } from '../components/Toast.js';
import { usePoll } from '../components/usePoll.js';
import { useLocale, useT } from '../i18n/index.js';
import { href, open as openRoute, ROUTES } from '../routes.js';
import { archivePerson, getPerson, type PersonBrief, type PersonTimelineItem } from '../api/persons.js';
import { confirmAffiliation } from '../api/affiliations.js';
import { confirmFact, createFact } from '../api/facts.js';
import { writingProfile } from '../api/mail.js';
import { startRun } from '../api/runs.js';
import { tCtx } from '../../shared/i18n.js';
import { personLabelFor } from '../../shared/person-raster.v1.js';
import type { RasterLang } from '../../shared/raster.v1.js';
import type {
  AffiliationView, EvidenceKind, Fact, PersonProfileResponse, WritingProfile as WritingProfileData,
} from '../../shared/types.js';
import type { PageProps } from './registry.js';
import './PersonPage.de.js';
import './PersonPage.fr.js';

/**
 * Timeline is the interactions agent's file (BUILD-BRIEF §6) and its agreed prop is `items`.
 * The cast is the seam between two agents building in parallel: it lets this page be written
 * against the agreed shape while the scaffold's placeholder still declares the old one, and it
 * disappears the day that file lands. It is deliberately the ONLY cast on this page.
 */
const Timeline = TimelineComponent as unknown as ComponentType<{ items: PersonTimelineItem[] }>;

const POLL_MS = 30_000;
const PROFILE_POLL_MS = 120_000;

/** The four columns of "what we currently know", in the order they are read. */
const GROUPS = ['Identity', 'Affiliations', 'Relationship', 'Research'] as const;
type Group = (typeof GROUPS)[number];

/**
 * A dotted key's first segment decides its column. An unrecognised prefix lands under
 * Research, because "somebody looked this up and we have not filed it properly" is the honest
 * reading of a key this catalogue does not know.
 */
const GROUP_OF_PREFIX: Record<string, Group> = {
  identity: 'Identity',
  contact: 'Identity',
  relationship: 'Relationship',
  persona: 'Relationship',
  research: 'Research',
};

const groupOf = (key: string): Group => GROUP_OF_PREFIX[key.split('.')[0]] ?? 'Research';

/** `identity.source_url` → 'source url'. The column already says which family it is in. */
const factLabel = (key: string): string => {
  const parts = key.split('.');
  return (parts.length > 1 ? parts.slice(1).join(' ') : key).replace(/_/g, ' ');
};

const isUrl = (v: string | null): boolean => !!v && /^https?:\/\//i.test(v);
const isEmail = (v: string | null): boolean => !!v && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);

const EVIDENCE_KINDS: EvidenceKind[] = ['manual', 'url', 'snapshot', 'interaction', 'document'];

export default function PersonPage({ personId }: PageProps) {
  const t = useT();
  const { fmtDate, tp, lang } = useLocale();
  const toast = useToast();

  const id = personId ?? 0;
  const load = useCallback(() => getPerson(id), [id]);
  const { data: brief, loading, error, notice, refresh } = usePoll<PersonBrief>(load, POLL_MS);
  // THREADS comes from the mail service's profile of this person (BUILD-BRIEF-R2 §7): a second,
  // slower poll. A failed poll is silence — the cell then reads "not collected", never a zero.
  const loadProfile = useCallback(() => writingProfile(id), [id]);
  const profile = usePoll<WritingProfileData>(loadProfile, PROFILE_POLL_MS);

  const [onlyUnconfirmed, setOnlyUnconfirmed] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [archiveOpen, setArchiveOpen] = useState(false);
  const [researching, setResearching] = useState(false);
  const [draft, setDraft] = useState<{ key: string; value: string; evidence_kind: EvidenceKind; evidence_ref: string }>(
    { key: '', value: '', evidence_kind: 'manual', evidence_ref: '' },
  );
  // THE RASTER (BUILD-BRIEF-R7 §6.1). PersonRasterZone loads it and hands it back here, so the
  // mast can print `buying_influence` beside the tier and the contact card can read its cells
  // without a second request. `rasterReload` is bumped after the card PATCHes, so the raster
  // re-reads the declared facts that write wrote.
  const [raster, setRaster] = useState<PersonProfileResponse | null>(null);
  const [rasterReload, setRasterReload] = useState(0);
  const afterContactSave = useCallback(async () => {
    await refresh();
    setRasterReload((n) => n + 1);
  }, [refresh]);


  /** The claim tally the zone head prints: ▪ held · ◇ proposed, over facts AND affiliations. */
  const columns = useMemo(() => {
    const out: Record<Group, ReactNode[]> = { Identity: [], Affiliations: [], Relationship: [], Research: [] };
    if (!brief) return out;

    for (const f of brief.facts) {
      if (onlyUnconfirmed && f.confirmed_at) continue;
      out[groupOf(f.key)].push(<FactRow key={`f${f.id}`} fact={f} busy={busy} onConfirmed={refresh} setBusy={setBusy} />);
    }
    for (const a of brief.affiliations) {
      if (onlyUnconfirmed && a.status !== 'proposed') continue;
      out.Affiliations.push(<AffiliationRow key={`a${a.id}`} aff={a} busy={busy} onConfirmed={refresh} setBusy={setBusy} />);
    }
    return out;
    // `busy` and `refresh` are stable enough for this list; the rows are cheap to rebuild.
  }, [brief, onlyUnconfirmed, busy, refresh]);

  if (!personId) {
    return (
      <PageShell>
        <Mast title={t('Person')} sub={t('no person was addressed')} />
        <EmptyState
          kind="error"
          title={t('This address names no person')}
          detail={t('A person brief is opened as #person?id=N from a row on the People board.')}
        />
      </PageShell>
    );
  }

  if (!brief) {
    return (
      <PageShell>
        <a className="back" href={href(ROUTES.people())}>← {t('People')}</a>
        <Mast title={t('Person')} sub={loading ? t('reading the record…') : t('the record did not answer')} />
        {error && !loading ? (
          <EmptyState
            kind="error"
            title={t('Could not read this person')}
            detail={<>GET /api/persons/{personId} — {error}</>}
            onRetry={() => { void refresh(); }}
          />
        ) : (
          <Sheet pad><p className="zone-empty">{t('Loading…')}</p></Sheet>
        )}
      </PageShell>
    );
  }

  const p = brief.person;
  const state: 'late' | 'cooling' | 'warm' | 'quiet' =
    brief.ball.late ? 'late' : brief.cooling ? 'cooling' : brief.days_since !== null ? 'warm' : 'quiet';
  const stateLabel = { late: 'late', cooling: 'cooling', warm: 'warm', quiet: 'quiet' }[state];

  const heldClaims = brief.confirmed.total;
  const proposedClaims = brief.unheld.total;

  // The buying influence the mast prints beside the tier: the table's reading (computed, no
  // mark) or a held fact on the cell. Absent until the raster answers — no guess in the meantime.
  const buyingCell = cellOf(raster, 'job', 'buying_influence');
  const buying = buyingCell && (buyingCell.state === 'held' || buyingCell.state === 'computed') ? buyingCell : null;

  const submitFact = async () => {
    const key = draft.key.trim();
    if (!key) { toast('err', t('A fact needs a key, for example identity.email.')); return; }
    try {
      await createFact({
        subject_kind: 'person',
        subject_id: p.id,
        key,
        value: draft.value.trim() || null,
        evidence_kind: draft.evidence_kind,
        evidence_ref: draft.evidence_ref.trim() || null,
      });
      setAddOpen(false);
      setDraft({ key: '', value: '', evidence_kind: 'manual', evidence_ref: '' });
      toast('ok', t('{key} written. Whatever the key said before is superseded, not erased.', { key }));
      await refresh();
    } catch (err) {
      toast('err', err instanceof Error ? err.message : String(err));
    }
  };

  /**
   * "Research" (BUILD-BRIEF-R4 §4): one `deep_research` run on this person — the employer and the
   * role read off the organisation's own pages and the press, never LinkedIn, and every one of
   * them a PROPOSAL. It answers when queued, not when done, and then opens the run's desk so the
   * operator can watch it work.
   */
  const research = async () => {
    setResearching(true);
    try {
      const run = await startRun({ skill: 'deep_research', subject_kind: 'person', subject_id: p.id });
      toast('ok', t('Research {ref} queued. What it can cite arrives in Review as a proposal; nothing is written until you hold it.', { ref: run.ref }));
      if (run.id) openRoute(ROUTES.run({ id: run.id }));
    } catch (err) {
      toast('err', err instanceof Error ? err.message : String(err));
    } finally {
      setResearching(false);
    }
  };

  const doArchive = async () => {
    try {
      await archivePerson(p.id);
      setArchiveOpen(false);
      toast('ok', t('{name} is archived. The record keeps everything.', { name: p.name }));
      openRoute(ROUTES.people());
    } catch (err) {
      toast('err', err instanceof Error ? err.message : String(err));
    }
  };

  // ── the rail ──
  const dayDetail = brief.last_interaction
    ? [
        `${t(brief.last_interaction.kind)} ${fmtDate(brief.last_interaction.occurred_on)}`,
        brief.previous_interaction
          ? `${t(brief.previous_interaction.kind)} ${fmtDate(brief.previous_interaction.occurred_on)}`
          : null,
      ].filter(Boolean).join(' · ')
    : t('nothing has been logged against them');

  const ballDetail = brief.ball.owner
    ? t('{us} with us · {them} with them', { us: brief.ball.us, them: brief.ball.them })
    : t('no open commitment either way');

  // THREADS: counted by the mail service. `profile.data` absent = nobody counted (unknown);
  // present with zero messages = counted and zero (nil). The two must never look the same.
  const mails = profile.data ? profile.data.messages_in + profile.data.messages_out : null;
  // Nothing logged, nothing owed, no mail counted: the relationship zones have nothing to say yet.
  const noRelationship = brief.interaction_count === 0 && !brief.next_move && !brief.ball.owner && (mails === null || mails === 0);
  const threadsDetail = profile.data === null
    ? (profile.error ? t('the mail service did not answer') : t('counting the correspondence…'))
    : mails === 0
      ? t('no mail with this person on record')
      : [
          t('{n} from them', { n: profile.data.messages_in }),
          t('{n} from you', { n: profile.data.messages_out }),
          profile.data.median_reply_hours_theirs !== null ? t('their median reply {h} h', { h: profile.data.median_reply_hours_theirs }) : null,
        ].filter(Boolean).join(' · ');

  return (
    <PageShell>
      <a className="back" href={href(ROUTES.people())}>← {t('People')}</a>

      <Mast
        kicker={
          <>
            {t('Person')} ·{' '}
            {/* the kicker is a .u-label, which uppercases and tracks; a ref is neither */}
            <span style={{ letterSpacing: 0, textTransform: 'none' }}><Ref>{p.ref}</Ref></span>
            {/* the organisation-ref hook: a stable slot round 3 fills further; the ref alone for now */}
            {brief.organisation && (
              <span data-slot="org-ref" style={{ letterSpacing: 0, textTransform: 'none' }}>
                {' '}· <Ref>{brief.organisation.ref}</Ref>
              </span>
            )}
          </>
        }
        title={
          <>
            <PersonAvatar id={p.id} name={p.name} /> {p.name}
            <span className={`statedot${state === 'quiet' ? ' statedot--idle' : ''}`} />
            <span className="statelabel">{t(stateLabel)}</span>
          </>
        }
        sub={
          <>
            {brief.organisation ? (
              <>
                <Att kind={brief.organisation.status === 'held' ? 'held' : 'proposed'} />{' '}
                {brief.organisation.role_title ?? t('role not named')}
                <Sep />
                <Lnk href={href(ROUTES.org({ id: brief.organisation.id }))}>{brief.organisation.name}</Lnk>
              </>
            ) : (
              <>{t('no current affiliation on the record')}</>
            )}
            <Sep />
            {p.language} · {p.register ? (p.register === 'sie' ? 'Sie' : 'Du') : t('register not set')}
            <Sep />
            {/* the tier slot — and, once the raster has answered, the person's BUYING INFLUENCE
                beside it (R7 §6.1): the table's reading carries no mark, a fact somebody held
                carries ▪, a fact nobody has held yet carries ◇ (the same `cell.held` test the
                raster below makes), and the estimate says so. The label is the module's own. */}
            <span data-slot="tier">
              {t('tier')} {p.tier}
              {buying && buying.value && (
                <>
                  {' · '}
                  {buying.state === 'held' && <><Att kind={buying.held ? 'held' : 'proposed'} />{' '}</>}
                  <span title={buying.basis ?? buying.evidence?.quote ?? undefined}>
                    {personLabelFor(lang as RasterLang, 'job', 'buying_influence', buying.value)}
                  </span>
                  {buying.method === 'estimated' && <> <Tag tone="ghost">{t('estimated')}</Tag></>}
                </>
              )}
            </span> · {t('cadence')} <Fig unit="d">{p.cadence_days}</Fig>
            {brief.unheld.total > 0 && (
              <>
                <Sep />
                <span className="tag-unheld">◇ {t('{n} unconfirmed', { n: brief.unheld.total })}</span>
              </>
            )}
            {/* ORIGIN (BUILD-BRIEF-R3b §2b) — where this person entered the record, derived
                server-side: the earliest interaction that names them, or the import that wrote
                the row. The parts are rendered rather than the server's composed `label`,
                because the date is a QUANTITY (`<Fig>`) and the interaction ref an IDENTITY
                (`<Ref>`); the label stands in only where the parts are missing. A brief that
                carries no origin shows NO line — nobody derived it, and a guess is a claim. */}
            {brief.origin && (
              <>
                <Sep />
                <span title={t('the first thing the record knows about this person')}>
                  {t('Origin')}{' '}
                  {brief.origin.kind === 'interaction'
                    ? (brief.origin.title ?? brief.origin.label)
                    : brief.origin.principal
                      ? `${t(brief.origin.kind)} · ${brief.origin.principal}`
                      : t(brief.origin.kind)}
                  {brief.origin.date && <> <Fig>{fmtDate(brief.origin.date)}</Fig></>}
                  {brief.origin.ref && <> <Ref>{brief.origin.ref}</Ref></>}
                </span>
              </>
            )}
            {p.do_not_contact && (<><Sep /><Tag tone="broken">{t('do not contact')}</Tag></>)}
          </>
        }
        verbs={
          <>
            {notice && <span className="zone-note">{notice}</span>}
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => setArchiveOpen(true)}>{t('Archive')}</button>
            <button
              type="button"
              className="btn"
              disabled={researching}
              title={t('Send an agent to read the web for this person: the employer and the role from the organisation’s own pages and the press, never LinkedIn. Everything arrives in Review as a proposal.')}
              onClick={() => { void research(); }}
            >
              {researching ? t('Queuing…') : tCtx(t, 'Research', 'verb')}
            </button>
            <button type="button" className="btn btn-accent" onClick={() => setAddOpen(true)}>{t('Add fact')}</button>
          </>
        }
      />

      <Rail>
        <RailCell
          label={t('Day')}
          value={brief.days_since === null ? '—' : brief.days_since}
          unit={brief.days_since === null ? undefined : t('since last touch')}
          tone={brief.days_since === null ? 'nil' : brief.cooling ? 'attend' : 'ink'}
          detail={dayDetail}
        />
        <RailCell
          label={t('Ball')}
          value={brief.ball.owner ? t(brief.ball.owner) : '—'}
          unit={brief.ball.due_on ? `${brief.ball.late ? t('was due') : t('due')} ${fmtDate(brief.ball.due_on)}` : undefined}
          tone={brief.ball.late ? 'broken' : brief.ball.owner === 'us' ? 'attend' : brief.ball.owner ? 'ink' : 'nil'}
          detail={ballDetail}
        />
        {/* Counted by the mail service. Unknown until it answers; nil when it counted zero. */}
        <RailCell
          label={t('Threads')}
          value={mails === null ? t('not collected') : <Fig>{mails}</Fig>}
          unit={mails ? t('mails') : undefined}
          tone={mails === null ? 'unknown' : mails === 0 ? 'nil' : 'ink'}
          detail={threadsDetail}
        />
        <RailCell
          label={t('Sources')}
          value={<Fig>{brief.sources.total}</Fig>}
          tone={brief.sources.total ? 'ink' : 'nil'}
          // Two counts, two plurals. A fixed string read '1 interactions' on every one of the
          // 279 seeded people, who each have exactly one.
          detail={`${tp(brief.sources.facts_with_evidence, '{n} fact with evidence', '{n} facts with evidence')} · ${tp(brief.sources.interactions, '{n} interaction', '{n} interactions')}`}
        />
        <RailCell
          label={t('Unheld')}
          value={<Fig>{brief.unheld.total}</Fig>}
          tone={brief.unheld.total ? 'attend' : 'nil'}
          detail={brief.unheld.total
            ? `${tp(brief.unheld.facts, '{n} fact', '{n} facts')} · ${tp(brief.unheld.affiliations, '{n} affiliation', '{n} affiliations')} · ${t('nobody has held them')}`
            : t('every claim on this page is held')}
        />
      </Rail>

      {/* ── next move ───────────────────────────────────────────────────── */}
      {/* ── the record ───────────────────────────────────────────────────── */}
      <Zone id="facts">
        <ZoneHead
          label={t('What we currently know')}
          count={<><Att kind="held" /> {heldClaims} <Sep /> <Att kind="proposed" /> {proposedClaims}</>}
        >
          <button
            type="button"
            className={`btn btn-sm btn-ghost${onlyUnconfirmed ? ' active' : ''}`}
            aria-pressed={onlyUnconfirmed}
            disabled={proposedClaims === 0}
            onClick={() => setOnlyUnconfirmed((v) => !v)}
          >
            {onlyUnconfirmed
              ? t('Show everything')
              : t('Only unconfirmed ({n})', { n: proposedClaims })}
          </button>
        </ZoneHead>
        {/* THE BASICS FIRST (the operator, 2026-09-06: "when I get into a record I don't see
            anything"): the contact card — the person's own columns and the raster's contact
            cells, each with its mark and its method, each editable — reads before any filed
            fact. An empty value says "not collected", never nothing. */}
        <ContactCard person={p} profile={raster} writing={profile.data} onSaved={afterContactSave} />
        <Sheet pad className="facts">
          {GROUPS.map((g) => (
            <div className="fgroup" key={g}>
              <div className="fgroup-h u-label">{t(g)}</div>
              {columns[g].length ? columns[g] : (
                <div className="fact">
                  <span className="fk">—</span>
                  <span className="d">
                    {onlyUnconfirmed ? t('nothing unconfirmed here') : t('nothing on record')}
                  </span>
                  <span className="fmeta" />
                </div>
              )}
            </div>
          ))}
        </Sheet>
      </Zone>

      {/* ── the raster · five blocks, every cell in its state (R7 §6.1). "Fill" and the mast's
          "Research" are the same function: one start path, one toast. ─────────────────── */}
      <PersonRasterZone
        personId={p.id}
        onProfile={setRaster}
        onFill={() => { void research(); }}
        filling={researching}
        reloadKey={rasterReload}
      />

      {/* ── the interactions ─────────────────────────────────────────────── */}
      <Zone id="latest">
        <ZoneHead
          label={t('Latest')}
          count={<Fig>{brief.interaction_count}</Fig>}
          note={brief.interaction_count > brief.interactions.length
            ? t('newest {n} of {total}', { n: brief.interactions.length, total: brief.interaction_count })
            : undefined}
        />
        {brief.interactions.length ? (
          <Timeline items={brief.interactions} />
        ) : (
          <p className="zone-empty">
            {t('No interaction names this person. A meeting, a mail or a note becomes an interaction with them as a participant; the seed import files the two research meetings that way.')}
          </p>
        )}
      </Zone>

      {/* ── the relationship · next move, why now, how you write — ONE quiet line while nothing
          has passed between you (2026-09-06): three empty zones told the operator nothing and hid
          the record under them. ─────────────────────────────────────────────── */}
      {noRelationship ? (
        <Zone id="relationship">
          <ZoneHead label={t('Relationship')} />
          <p className="zone-empty">
            {t('Nothing has passed between you and this person yet. The next move, the reason to act and the writing profile appear here once something does.')}
          </p>
        </Zone>
      ) : (
        <>
        <Zone id="next-move">
          <ZoneHead label={t('Next move')} note={t('ranked · the page arranges, it does not decide')} />
          {brief.next_move ? (
            <Sheet edge pad>
              <div className="move-head">
                <h2>{brief.next_move.title}</h2>
                <div className="move-cta">
                  <Ref>{brief.next_move.ref}</Ref>
                </div>
              </div>
              <p className="read" style={{ margin: '8px 0 0' }}>{brief.next_move.detail}</p>
              <div className="chips">
                <Tag tone={brief.next_move.owner === 'us' ? 'attend' : 'neutral'}>
                  {t('ball')} {t(brief.next_move.owner)}
                </Tag>
                {brief.next_move.due_on && (
                  <Tag tone={brief.next_move.late ? 'broken' : 'neutral'}>
                    {brief.next_move.late ? t('was due') : t('due')} {fmtDate(brief.next_move.due_on)}
                  </Tag>
                )}
                {brief.next_move.source_ref && <Tag tone="ghost">{brief.next_move.source_ref}</Tag>}
              </div>
            </Sheet>
          ) : (
            <p className="zone-empty">
              {t('Nothing is owed either way. A move appears here when a commitment is logged against this person — from a meeting, a mail or by hand — so the next thing to do is always something somebody actually said.')}
            </p>
          )}
        </Zone>

        {/* ── why now · the scoring agent's reason to act, rules first, model second ── */}
        {/* WhyNow owns its own Zone/ZoneHead (id="why-now") — do not wrap it here. */}
        <WhyNow personId={p.id} />

        {/* ── the writing profile · measured by the mail service (§7) ─────────── */}
        <Zone id="writing">
          <ZoneHead
            label={t('How you two write')}
            count={mails === null ? undefined : <Fig>{tp(mails, '{n} mail', '{n} mails')}</Fig>}
            note={t('the writing profile drives every draft to them')}
          />
          <WritingProfile personId={p.id} />
        </Zone>

        </>
      )}

      {/* ── the dossier · a document with its receipts, never held (§9) ──── */}
      <DossierZone subjectKind="person" subjectId={p.id} />

      {/* ── the persona · proposed traits, each with a quote (§10) ───────── */}
      <PersonaZone personId={p.id} />

      <ConfirmModal
        open={addOpen}
        title={t('Add fact')}
        confirmLabel={t('Write it')}
        onCancel={() => setAddOpen(false)}
        onConfirm={() => { void submitFact(); }}
      >
        <div className="form-group">
          <label className="form-label" htmlFor="fk">{t('Key')}</label>
          <input
            id="fk"
            className="form-input"
            value={draft.key}
            autoFocus
            placeholder="identity.email"
            onChange={(e) => setDraft((d) => ({ ...d, key: e.target.value }))}
          />
        </div>
        <div className="form-group">
          <label className="form-label" htmlFor="fvl">{t('Value')}</label>
          <input
            id="fvl"
            className="form-input"
            value={draft.value}
            onChange={(e) => setDraft((d) => ({ ...d, value: e.target.value }))}
          />
        </div>
        <div className="form-group">
          <label className="form-label" htmlFor="fek">{t('Evidence')}</label>
          <select
            id="fek"
            className="form-select"
            value={draft.evidence_kind}
            onChange={(e) => setDraft((d) => ({ ...d, evidence_kind: e.target.value as EvidenceKind }))}
          >
            {EVIDENCE_KINDS.map((k) => <option key={k} value={k}>{t(k)}</option>)}
          </select>
        </div>
        <div className="form-group">
          <label className="form-label" htmlFor="fer">{t('Evidence reference')}</label>
          <input
            id="fer"
            className="form-input"
            value={draft.evidence_ref}
            placeholder={t('a URL, a snapshot id, an interaction ref')}
            onChange={(e) => setDraft((d) => ({ ...d, evidence_ref: e.target.value }))}
          />
        </div>
        <p className="zone-empty" style={{ marginTop: 12 }}>
          {t('Writing a key that already has a value supersedes the old one. Nothing is overwritten and nothing is lost.')}
        </p>
      </ConfirmModal>

      <ConfirmModal
        open={archiveOpen}
        title={t('Archive {name}?', { name: p.name })}
        confirmLabel={t('Archive')}
        danger
        onCancel={() => setArchiveOpen(false)}
        onConfirm={() => { void doArchive(); }}
      >
        <p className="read">
          {t('They leave the register and the board. Every fact, affiliation, interaction and commitment stays exactly where it is, and the archive can be searched with status=archived.')}
        </p>
      </ConfirmModal>
    </PageShell>
  );
}

// ═══ the rows of the record ═════════════════════════════════════════════════

/** One current fact. ◇ carries the Confirm that makes it ▪. */
function FactRow({ fact, busy, setBusy, onConfirmed }: {
  fact: Fact;
  busy: string | null;
  setBusy: (v: string | null) => void;
  onConfirmed: () => Promise<void>;
}) {
  const t = useT();
  const { fmtDate } = useLocale();
  const toast = useToast();
  const key = `f${fact.id}`;
  const held = fact.confirmed_at !== null;

  const confirm = async () => {
    setBusy(key);
    try {
      await confirmFact(fact.id);
      toast('ok', t('{key} is held.', { key: fact.key }));
      await onConfirmed();
    } catch (err) {
      toast('err', err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(null);
    }
  };

  const value = fact.value;
  const rendered: ReactNode = value === null
    ? <span className="n-none">{t('no value')}</span>
    : isUrl(value)
      ? <a className="lnk" href={value} target="_blank" rel="noreferrer noopener"><Ref>{value}</Ref></a>
      : isEmail(value) ? <Ref>{value}</Ref> : value;

  return (
    <div className="fact">
      <span className="fk">{factLabel(fact.key)}</span>
      <span>
        <Att kind={held ? 'held' : 'proposed'} /> {rendered}
        {!held && (
          <>
            {' '}<span className="tag-unheld">{t('unconfirmed')}</span>{' '}
            <button type="button" className="lnk" disabled={busy === key} onClick={() => { void confirm(); }}>
              {busy === key ? t('Saving…') : t('Confirm')}
            </button>
          </>
        )}
        {fact.quote && <blockquote className="ev">{fact.quote}</blockquote>}
      </span>
      <span className="fmeta">
        {fact.as_of
          ? <Fig>{fmtDate(fact.as_of)}</Fig>
          : fact.evidence_ref
            ? <Ref>{fact.evidence_ref}</Ref>
            : t(fact.created_by_kind)}
      </span>
    </div>
  );
}

/** One affiliation. `proposed` is ◇ and carries the Confirm that PATCHes it to `held`. */
function AffiliationRow({ aff, busy, setBusy, onConfirmed }: {
  aff: AffiliationView;
  busy: string | null;
  setBusy: (v: string | null) => void;
  onConfirmed: () => Promise<void>;
}) {
  const t = useT();
  const { fmtDate } = useLocale();
  const toast = useToast();
  const key = `a${aff.id}`;
  const held = aff.status === 'held';

  const confirm = async () => {
    setBusy(key);
    try {
      await confirmAffiliation(aff.id);
      toast('ok', t('{org} is held.', { org: aff.organisation_name ?? '' }));
      await onConfirmed();
    } catch (err) {
      toast('err', err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(null);
    }
  };

  const slot = aff.status === 'retracted' ? 'retracted' : aff.is_current ? 'current' : 'former';

  return (
    <div className="fact">
      <span className="fk">{t(slot)}</span>
      <span>
        <Att kind={held ? 'held' : 'proposed'} />{' '}
        <Lnk href={href(ROUTES.org({ id: aff.organisation_id }))}>{aff.organisation_name}</Lnk>
        {aff.role_title && <> · {aff.role_title}</>}
        {aff.started_on && <> · {t('since')} <Fig>{fmtDate(aff.started_on)}</Fig></>}
        {aff.ended_on && <> – <Fig>{fmtDate(aff.ended_on)}</Fig></>}
        {aff.status === 'proposed' && (
          <>
            {' '}<span className="tag-unheld">{t('unconfirmed')}</span>{' '}
            <button type="button" className="lnk" disabled={busy === key} onClick={() => { void confirm(); }}>
              {busy === key ? t('Saving…') : t('Confirm')}
            </button>
          </>
        )}
      </span>
      <span className="fmeta">
        {aff.source_url
          ? <a className="lnk" href={aff.source_url} target="_blank" rel="noreferrer noopener">{t(aff.source_kind ?? 'manual')}</a>
          : aff.source_kind ? t(aff.source_kind) : t(aff.created_by_kind)}
      </span>
    </div>
  );
}
